import React, { useEffect, useMemo, useState } from 'react'
import {
  type Column as Cell,
  flexRender,
  type Row,
} from '@tanstack/react-table'
import { get } from 'lodash'
import moment from 'moment'

import {
  Alert,
  Button,
  DatePicker,
  FormFooter,
  Icon,
  Mdash,
  TextInput,
  trimText,
  useFocus,
} from '../../../../module'
import styles from './_spreadsheet-cell.module.scss'
import EditCellSelect from './EditCellSelect'
import EditCellBoolean from './EditCellBoolean'
import EditCellArray from './EditCellArray'
import {
  getValidationRules,
  type InputRule,
  isBoolean,
  isDate,
  isSelect,
  type TypeCustomRules,
  validateInput,
} from './validationService'
import EditCellSideDrawer from './EditCellSideDrawer'
import { c } from '../../../../translations/LibraryTranslationService'

export type Column = {
  id: string
  columnDef: {
    header?: {
      heading?: string
    }
    meta?: {
      editable?: boolean
      disableTrimText?: boolean
    }
    accessorKey: string
  }
}

export type SpreadsheetCellProps<TData extends object> = {
  getValue: () => string
  table: {
    options: {
      meta?: {
        inputRules: {
          [key: string]: InputRule
        }
        isReviewMode: boolean
        updateData: (
          rowIndex: number,
          columnId: string,
          value: string | number | boolean | Date,
        ) => void
      }
    }
    getAllColumns: () => Cell<TData>[]
    getColumn: (columnId: string) => Cell<TData> | undefined
  }
  row: Row<TData> & {
    original: {
      editable: boolean
      disableTrimText?: boolean
      isOptional?: boolean
    }
  }
  column: Column
  /** Optional children can be passed in for custom JSX content */
  children?: React.ReactNode
  isEdited?: (val: boolean) => void
}

const SpreadsheetCell = <TData extends object>({
  getValue,
  table,
  row,
  column,
  children,
  isEdited,
}: SpreadsheetCellProps<TData>) => {
  const [isEditOpen, setIsEditOpen] = useState(false),
    [isReviewOpen, setIsReviewOpen] = useState(false),
    initialValue = getValue(),
    [value, setValue] = useState<
      string | number | boolean | Date | Array<string>
    >(initialValue),
    [isUpdated, setIsUpdated] = useState<boolean>(false)

  const [cellValidationRule, setCellValidationRule] = useState<TypeCustomRules>(
    {},
  )

  const cellKey = column.columnDef.accessorKey.split('.')[0],
    isEditable = get(row.original, [cellKey, 'editable']),
    inputRules = table.options?.meta?.inputRules,
    isReviewMode = table.options?.meta?.isReviewMode,
    isDisableTrimText =
      get(row.original, [cellKey, 'disableTrimText']) ?? false,
    isOptional = get(row.original, [cellKey, 'isOptional']) ?? false

  const defaultErrors = useMemo(
    () => get(row.original, [cellKey, 'error']) ?? '',
    [cellKey, row.original],
  )
  const productCellIndex = table
      .getAllColumns()
      .findIndex((cell) => cell.id === 'product'),
    isProductCellExists = get(row.original, 'product'),
    primaryProductCell = row.getAllCells()[productCellIndex]

  const getObjectByKey = (key: string) => {
    return inputRules && inputRules?.[key]
  }

  const close = () => {
    setIsEditOpen(false)
    setValue(initialValue)
    setIsUpdated(false)
  }

  const onBlur = () => {
    table.options.meta?.updateData?.(
      row.index,
      column.columnDef.accessorKey,
      validationRules?.data_type === 'float' ||
        validationRules?.data_type === 'integer' ||
        validationRules?.data_type === 'number'
        ? Number(value)
        : typeof value === 'string'
          ? value
          : value.toString(),
    )
  }

  const [errorText, setErrorText] = useState('')

  const submitForm = () => {
    onBlur()
    close()
    setIsUpdated(true)
  }

  const [isInvalid, setIsInvalid] = useState<boolean>(true)

  const validationRules = getObjectByKey(column?.id)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => isEdited && isEdited(!isInvalid), [isEdited, isInvalid])

  useEffect(() => {
    if (defaultErrors && initialValue === value?.toString()) {
      setErrorText(defaultErrors)
      setIsInvalid(initialValue === value)
    } else {
      const checkValidation = validateInput(
        value?.toString(),
        validationRules as InputRule,
        cellValidationRule,
      )
      setIsInvalid(checkValidation?.valid || initialValue === value)
      setErrorText(checkValidation?.errorText ?? '')
    }
  }, [cellValidationRule, defaultErrors, initialValue, validationRules, value])

  const isDateResult = isDate(initialValue, validationRules)
  const isValidDate = isDateResult && isDateResult.isValid()

  // Data Type - Array
  const isArray = validationRules?.data_type === 'array'

  useEffect(() => {
    if (validationRules?.validations) {
      const rules: TypeCustomRules = getValidationRules(
        validationRules?.validations,
      )
      setCellValidationRule(rules)
    }
  }, [validationRules?.validations])

  // Focusing the input field on open
  const inputRef = useFocus(isEditOpen),
    TRIM_TEXT_CHARACTER_LENGTH = 25

  const getValueBykey = (options: TypeCustomRules, key: string) => {
    return options?.[key] ?? key
  }

  const isSelectRule = isSelect(validationRules)
  const isBooleanRule = isBoolean(validationRules)

  const updatedHeaderValue = useMemo(() => {
    return c('updatedCellHeader', {
      header: column?.columnDef?.header?.heading ?? '',
    })
  }, [column?.columnDef?.header?.heading])

  const renderReviewCellContent = () => {
    return (
      <div className={styles.viewCellPopoverContent}>
        {Array.isArray(initialValue)
          ? initialValue.join(' ')
          : isSelectRule || isBooleanRule
            ? getValueBykey(
                validationRules?.options as TypeCustomRules,
                initialValue,
              )
            : initialValue}
      </div>
    )
  }
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    moment(initialValue, 'DD-MM-YYYY').toDate(),
  )
  const renderEditCellContent = () => {
    return (
      <>
        {isSelectRule ? (
          <EditCellSelect
            callbackValue={setValue}
            initialValue={initialValue}
            validationRules={validationRules || {}}
            header={updatedHeaderValue}
          />
        ) : isBooleanRule ? (
          <EditCellBoolean
            callbackValue={setValue}
            initialValue={initialValue}
            validationRules={validationRules || {}}
            header={updatedHeaderValue}
          />
        ) : isValidDate ? (
          <DatePicker
            type='single'
            selectedDate={selectedDate}
            onDateChange={(date) => {
              if (date instanceof Date) {
                setSelectedDate(date)
                setValue(moment(date).format('DD-MM-YYYY'))
              } else {
                setSelectedDate(undefined)
              }
            }}
          />
        ) : isArray && Array.isArray(initialValue) ? (
          <EditCellArray
            callbackValue={setValue}
            initialValue={initialValue}
            validationRules={validationRules}
            isInvalid={isInvalid}
            errorText={errorText}
            isVisible={isEditOpen}
            header={updatedHeaderValue}
          />
        ) : (
          <TextInput
            hasError={isInvalid}
            errorText={errorText}
            labelText={updatedHeaderValue}
            type={initialValue?.length > 100 ? 'textarea' : 'text'}
            value={value?.toString()}
            required={validationRules?.required}
            callout={(_, value) => {
              setValue(value)
            }}
            ref={inputRef}
            onReturnCallout={() => {
              if (!isInvalid) {
                submitForm()
              }
            }}
          />
        )}
      </>
    )
  }

  const renderPrimaryProductCell = () => {
    return (
      <div className={styles.primaryProductCell}>
        {flexRender(
          primaryProductCell.column.columnDef.cell,
          primaryProductCell.getContext(),
        )}
      </div>
    )
  }

  const renderOriginalValue = () => {
    return (
      <div className={styles.headerRow}>
        <span className={styles.cellLabel}>
          {column?.columnDef?.header?.heading ?? ''} - Original Value
        </span>
        {renderReviewCellContent()}
      </div>
    )
  }

  const renderUpdatedValue = () => {
    return <div className={styles.headerRow}>{renderEditCellContent()}</div>
  }

  const renderAlerts = () => {
    const text = isReviewMode ? c('errorCellNotEditable') : errorText

    return text ? (
      <div className={styles.alertStyle}>
        <Alert type={isReviewMode ? 'warning' : 'error'} text={text} />
      </div>
    ) : null
  }

  const renderSideDrawerContent = () => {
    return (
      <div>
        {isProductCellExists &&
          primaryProductCell &&
          renderPrimaryProductCell()}
        {renderAlerts()}
        {renderOriginalValue()}
        {!isReviewMode && isEditable && renderUpdatedValue()}
      </div>
    )
  }

  return (
    <>
      {children ? (
        <div className={styles.cell}>{children}</div>
      ) : isEditable && !isReviewMode ? (
        <>
          <Button
            as='unstyled'
            onClick={() => {
              setIsEditOpen(!isEditOpen)
            }}
            className={styles.cell}
          >
            {initialValue && !isUpdated ? (
              <span>
                {isValidDate
                  ? moment(initialValue).format('DD-MM-YYYY')
                  : isSelectRule || isBooleanRule
                    ? getValueBykey(
                        validationRules?.options as TypeCustomRules,
                        initialValue,
                      )
                    : isDisableTrimText
                      ? initialValue
                      : trimText(
                          initialValue.toString(),
                          TRIM_TEXT_CHARACTER_LENGTH,
                        )}
              </span>
            ) : (
              <span>
                {value?.toString()?.length === 0 && !isReviewMode ? (
                  isOptional ? (
                    <Mdash />
                  ) : (
                    <Icon icon='caution' iconSize='16px' color='dark-red' />
                  )
                ) : isSelectRule || isBooleanRule ? (
                  getValueBykey(
                    validationRules?.options as TypeCustomRules,
                    value.toString(),
                  )
                ) : isDisableTrimText ? (
                  value.toString()
                ) : (
                  trimText(value.toString(), TRIM_TEXT_CHARACTER_LENGTH)
                )}
              </span>
            )}
          </Button>
          <EditCellSideDrawer
            isOpen={isEditOpen}
            closeCallout={close}
            headerContent={c('editCell')}
            footerContent={
              <FormFooter
                cancelButtonProps={{
                  onClick: close,
                }}
                saveButtonProps={{
                  onClick: () => {
                    if (!isInvalid) {
                      submitForm()
                    }
                  },
                  children: 'Save',
                  disabled: isInvalid,
                }}
              />
            }
          >
            {renderEditCellContent()}
          </EditCellSideDrawer>
        </>
      ) : (
        <span className={styles.cell}>
          {isDisableTrimText ? (
            isSelectRule || isBooleanRule ? (
              getValueBykey(
                validationRules?.options as TypeCustomRules,
                initialValue,
              )
            ) : (
              initialValue
            )
          ) : (
            <>
              <Button
                as='unstyled'
                onClick={() => {
                  setIsReviewOpen(!isReviewOpen)
                }}
              >
                {trimText(
                  Array.isArray(initialValue)
                    ? initialValue.join(' ')
                    : isSelectRule || isBooleanRule
                      ? getValueBykey(
                          validationRules?.options as TypeCustomRules,
                          initialValue,
                        )
                      : initialValue,
                  TRIM_TEXT_CHARACTER_LENGTH,
                )}
              </Button>
              <EditCellSideDrawer
                isOpen={isReviewOpen}
                closeCallout={() => setIsReviewOpen(false)}
                headerContent={c('reviewCell')}
                footerContent={
                  <div className={styles.contentAtEnd}>
                    <Button
                      onClick={() => {
                        setIsReviewOpen(false)
                      }}
                    >
                      {c('close')}
                    </Button>
                  </div>
                }
              >
                {renderSideDrawerContent()}
              </EditCellSideDrawer>
            </>
          )}
        </span>
      )}
    </>
  )
}

export default SpreadsheetCell
